// frontend/src/pages/properties/PropertyUnitDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit, FiTrash2, FiUpload, FiX, FiHome,
  FiUser, FiFileText, FiMaximize2, FiBriefcase, FiGrid,
  FiCheck, FiAlertCircle, FiTool, FiPlus
} from 'react-icons/fi';
import { propertyUnitAPI, propertyAPI } from '../../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────
const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000'}${url}`;
  }
  return url;
};

const STATUS_CONFIG = {
  available:   { label: 'Disponible',   icon: <FiCheck />,       cls: 'unit-status-available' },
  occupied:    { label: 'Occupé',        icon: <FiUser />,        cls: 'unit-status-occupied' },
  reserved:    { label: 'Réservé',       icon: <FiAlertCircle />, cls: 'unit-status-reserved' },
  maintenance: { label: 'Maintenance',   icon: <FiTool />,        cls: 'unit-status-maintenance' },
  unavailable: { label: 'Indisponible',  icon: <FiX />,           cls: 'unit-status-unavailable' },
};

const UNIT_TYPE_LABELS = {
  apartment: 'Appartement',
  studio:    'Studio',
  office:    'Bureau',
  shop:      'Commerce / Magasin',
  other:     'Autre',
};

const UNIT_TYPE_ICONS = {
  apartment: <FiHome />,
  studio:    <FiHome />,
  office:    <FiBriefcase />,
  shop:      <FiGrid />,
  other:     <FiHome />,
};

// ─── Composant principal ─────────────────────────────────────────────────────
const PropertyUnitDetailPage = () => {
  const { id: propertyId, unitId } = useParams();
  const navigate = useNavigate();

  const [unit, setUnit]           = useState(null);
  const [property, setProperty]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showAddVideoUrl, setShowAddVideoUrl] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState({ url: '', title: '' });
  const [editForm, setEditForm]   = useState({
    unit_number: '', floor: '', unit_type: 'apartment', surface_area: '', num_rooms: '',
    num_bedrooms: '', num_bathrooms: '', is_furnished: false, monthly_rent: '',
    deposit_amount: '', agency_fee: '0', status: 'available', description: ''
  });

  // ── Chargement ──────────────────────────────────────────────────────────
  const loadUnit = async () => {
    try {
      const [uRes, pRes] = await Promise.all([
        propertyUnitAPI.getById(unitId),
        propertyAPI.getById(propertyId)
      ]);
      setUnit(uRes.data);
      setProperty(pRes.data);
    } catch (err) {
      console.error('Error loading unit:', err);
      navigate(`/properties/${propertyId}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, propertyId]);

  // ── Modal d'édition ─────────────────────────────────────────────────────
  const openEdit = () => {
    setEditForm({
      unit_number:    unit.unit_number || '',
      floor:          unit.floor !== null && unit.floor !== undefined ? unit.floor : '',
      unit_type:      unit.unit_type || 'apartment',
      surface_area:   unit.surface_area || '',
      num_rooms:      unit.num_rooms || '',
      num_bedrooms:   unit.num_bedrooms || '',
      num_bathrooms:  unit.num_bathrooms || '',
      is_furnished:   !!unit.is_furnished,
      monthly_rent:   unit.monthly_rent || '',
      deposit_amount: unit.deposit_amount || '',
      agency_fee:     unit.agency_fee != null ? String(unit.agency_fee) : (unit.charges != null ? String(unit.charges) : '0'),
      status:         unit.status || 'available',
      description:    unit.description || '',
    });
    setShowEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await propertyUnitAPI.update(unitId, editForm);
      setShowEditModal(false);
      await loadUnit();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // ── Suppression ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette unité ?')) return;
    try {
      await propertyUnitAPI.delete(unitId);
      navigate(`/properties/${propertyId}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // ── Upload images ────────────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const formData = new FormData();
    for (let f of files) formData.append('images', f);
    setUploadingImg(true);
    try {
      await propertyUnitAPI.uploadImages(unitId, formData);
      await loadUnit();
    } catch (err) {
      alert("Erreur lors de l'upload des images");
    } finally {
      setUploadingImg(false);
    }
  };

  const handleRemoveImage = async (imageId) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await propertyUnitAPI.removeImage(unitId, imageId);
      await loadUnit();
    } catch (err) {
      alert("Erreur lors de la suppression de l'image");
    }
  };

  // ── Upload vidéos ────────────────────────────────────────────────────────
  const handleVideoUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const formData = new FormData();
    for (let f of files) formData.append('videos', f);
    setUploadingVideo(true);
    try {
      await propertyUnitAPI.uploadVideos(unitId, formData);
      await loadUnit();
    } catch (err) {
      alert("Erreur lors de l'upload des vidéos");
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleAddVideoUrl = async (e) => {
    e.preventDefault();
    if (!videoUrlInput.url.trim()) return;
    try {
      await propertyUnitAPI.addVideoUrl(unitId, {
        url: videoUrlInput.url.trim(),
        title: videoUrlInput.title.trim() || 'Vidéo externe',
      });
      setVideoUrlInput({ url: '', title: '' });
      setShowAddVideoUrl(false);
      await loadUnit();
    } catch (err) {
      alert("Erreur lors de l'ajout de l'URL vidéo");
    }
  };

  const handleRemoveVideo = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    try {
      await propertyUnitAPI.removeVideo(unitId, videoId);
      await loadUnit();
    } catch (err) {
      alert("Erreur lors de la suppression de la vidéo");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!unit) return null;

  const status = unit.computed_status || unit.status || 'available';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.unavailable;

  const heroBg = {
    available:   'linear-gradient(135deg, #065f46 0%, #059669 100%)',
    occupied:    'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
    reserved:    'linear-gradient(135deg, #78350f 0%, #d97706 100%)',
    maintenance: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
    unavailable: 'linear-gradient(135deg, #374151 0%, #6b7280 100%)',
  }[status] || 'linear-gradient(135deg, #1e293b 0%, #334155 100%)';

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/properties/${propertyId}`)}>
            <FiArrowLeft /> Retour au bien
          </button>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              <Link to="/properties" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Biens</Link>
              {' › '}
              <Link to={`/properties/${propertyId}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                {property?.title || `Bien #${propertyId}`}
              </Link>
              {' › '} Unité {unit.unit_number}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(status === 'available' || status === 'reserved') && (
            <Link
              to={`/leases?property=${propertyId}&unit=${unitId}`}
              className="btn btn-success btn-sm"
            >
              <FiFileText /> Créer un bail
            </Link>
          )}
          <button className="btn btn-secondary btn-sm" onClick={openEdit}>
            <FiEdit /> Modifier
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDelete}
            disabled={status === 'occupied'}
            title={status === 'occupied' ? 'Impossible de supprimer une unité occupée' : ''}
          >
            <FiTrash2 /> Supprimer
          </button>
        </div>
      </div>

      {/* ── Hero Banner ── */}
      <div className="unit-detail-hero" style={{ background: heroBg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22, opacity: 0.8 }}>{UNIT_TYPE_ICONS[unit.unit_type] || <FiHome />}</span>
              <h1 style={{ color: 'white', margin: 0 }}>
                {UNIT_TYPE_LABELS[unit.unit_type] || 'Unité'} — {unit.unit_number}
              </h1>
            </div>
            <p className="sub">{property?.title} · {property?.address || property?.city || ''}</p>
            <span
              style={{
                background: 'rgba(255,255,255,0.18)',
                color: 'white',
                marginTop: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {statusCfg.icon} {statusCfg.label}
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: 'white', lineHeight: 1 }}>
              {parseInt(unit.monthly_rent || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
              XOF / mois
            </div>
            {(unit.agency_fee > 0 || unit.charges > 0) && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                + {parseInt(unit.agency_fee || unit.charges).toLocaleString()} XOF frais d'agence
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main Grid ── */}
      <div className="detail-grid">
        {/* Colonne gauche */}
        <div>
          {/* Informations de l'unité */}
          <div className="card">
            <div className="card-header"><h2>Informations</h2></div>
            <div className="card-body">
              <div className="detail-row">
                <div className="detail-label">N° Unité</div>
                <div className="detail-value"><strong>{unit.unit_number}</strong></div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Type</div>
                <div className="detail-value">{UNIT_TYPE_LABELS[unit.unit_type] || unit.unit_type}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Étage</div>
                <div className="detail-value">
                  {unit.floor !== null && unit.floor !== undefined ? `Étage ${unit.floor}` : 'RDC / Non spécifié'}
                </div>
              </div>
              {unit.surface_area && (
                <div className="detail-row">
                  <div className="detail-label">Surface</div>
                  <div className="detail-value">
                    <FiMaximize2 style={{ marginRight: 4, verticalAlign: 'middle' }} />{unit.surface_area} m²
                  </div>
                </div>
              )}
              {unit.num_rooms && (
                <div className="detail-row">
                  <div className="detail-label">Pièces</div>
                  <div className="detail-value">{unit.num_rooms} pièce(s)</div>
                </div>
              )}
              {unit.num_bedrooms !== undefined && unit.num_bedrooms !== null && (
                <div className="detail-row">
                  <div className="detail-label">Chambres</div>
                  <div className="detail-value">{unit.num_bedrooms}</div>
                </div>
              )}
              {unit.num_bathrooms !== undefined && unit.num_bathrooms !== null && (
                <div className="detail-row">
                  <div className="detail-label">Salles de bain</div>
                  <div className="detail-value">{unit.num_bathrooms}</div>
                </div>
              )}
              <div className="detail-row">
                <div className="detail-label">Meublé</div>
                <div className="detail-value">
                  {unit.is_furnished
                    ? <span className="badge badge-success">Oui</span>
                    : <span className="badge badge-secondary">Non</span>
                  }
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Statut</div>
                <div className="detail-value">
                  <span className={`unit-status-badge ${statusCfg.cls}`}>
                    {statusCfg.icon} {statusCfg.label}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Finances */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header"><h2>Finances</h2></div>
            <div className="card-body">
              <div className="detail-row">
                <div className="detail-label">Loyer mensuel</div>
                <div className="detail-value" style={{ fontWeight: 700, fontSize: 16, color: 'var(--primary)' }}>
                  {parseInt(unit.monthly_rent || 0).toLocaleString()} {unit.currency || 'XOF'}
                </div>
              </div>
              {(unit.agency_fee > 0 || unit.charges > 0) && (
                <div className="detail-row">
                  <div className="detail-label">Frais d'agence</div>
                  <div className="detail-value" style={{ fontWeight: 600 }}>
                    {parseInt(unit.agency_fee || unit.charges).toLocaleString()} {unit.currency || 'XOF'}
                  </div>
                </div>
              )}
              {unit.deposit_amount > 0 && (
                <div className="detail-row">
                  <div className="detail-label">Caution</div>
                  <div className="detail-value">
                    {parseInt(unit.deposit_amount).toLocaleString()} {unit.currency || 'XOF'}
                  </div>
                </div>
              )}
              <div className="detail-row">
                <div className="detail-label">Total initial (entrée)</div>
                <div className="detail-value" style={{ fontWeight: 700, color: '#15803d' }}>
                  {(parseInt(unit.monthly_rent || 0) + parseInt(unit.deposit_amount || 0) + parseInt(unit.agency_fee || unit.charges || 0)).toLocaleString()} {unit.currency || 'XOF'}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {unit.description && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header"><h2>Description</h2></div>
              <div className="card-body">
                <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text)', lineHeight: 1.6 }}>{unit.description}</p>
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite */}
        <div>
          {/* Locataire actuel */}
          <div className="card">
            <div className="card-header">
              <h2>Occupation actuelle</h2>
              {(status === 'available' || status === 'reserved') && (
                <Link to={`/leases?property=${propertyId}&unit=${unitId}`} className="btn btn-primary btn-sm">
                  <FiPlus /> Bail
                </Link>
              )}
            </div>
            <div className="card-body">
              {unit.tenant_first_name ? (
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                    border: '1px solid #bbf7d0',
                    borderRadius: 10, padding: '14px 16px', marginBottom: 14
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 700, fontSize: 18
                    }}>
                      {(unit.tenant_first_name[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>
                        {unit.tenant_first_name} {unit.tenant_last_name}
                      </div>
                      {unit.tenant_phone && (
                        <div style={{ fontSize: 13, color: '#166534', marginTop: 2 }}>
                          📞 {unit.tenant_phone}
                        </div>
                      )}
                    </div>
                  </div>
                  {unit.active_lease_id && (
                    <div>
                      <div className="detail-row">
                        <div className="detail-label">N° Contrat</div>
                        <div className="detail-value">
                          <Link
                            to={`/leases/${unit.active_lease_id}`}
                            style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}
                          >
                            {unit.active_lease_number || `#${unit.active_lease_id}`}
                          </Link>
                        </div>
                      </div>
                      {unit.lease_start_date && (
                        <div className="detail-row">
                          <div className="detail-label">Début du bail</div>
                          <div className="detail-value">
                            {new Date(unit.lease_start_date).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      )}
                      {unit.lease_end_date && (
                        <div className="detail-row">
                          <div className="detail-label">Fin du bail</div>
                          <div className="detail-value">
                            {new Date(unit.lease_end_date).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: '30px 20px' }}>
                  <FiUser size={28} style={{ opacity: 0.3, marginBottom: 10 }} />
                  <p>Aucun locataire — unité libre.</p>
                  {(status === 'available') && (
                    <Link
                      to={`/leases?property=${propertyId}&unit=${unitId}`}
                      className="btn btn-primary"
                      style={{ marginTop: 12 }}
                    >
                      <FiFileText /> Créer un bail
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Images */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <h2>Photos ({unit.images?.length || 0})</h2>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                {uploadingImg
                  ? <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  : <FiUpload />
                }
                {uploadingImg ? ' Upload...' : ' Ajouter'}
                <input
                  type="file" multiple accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingImg}
                />
              </label>
            </div>
            <div className="card-body">
              {unit.images && unit.images.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
                  {unit.images.map((img, idx) => (
                    <div
                      key={img.id}
                      style={{
                        position: 'relative', borderRadius: 8, overflow: 'hidden',
                        border: img.is_primary ? '2px solid var(--primary)' : '1px solid var(--border)',
                        aspectRatio: '1',
                      }}
                    >
                      <img
                        src={getFileUrl(img.url)}
                        alt={img.alt_text || `Photo ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={e => { e.target.src = '/placeholder-image.jpg'; e.target.onerror = null; }}
                      />
                      {img.is_primary && (
                        <span style={{
                          position: 'absolute', bottom: 4, left: 4,
                          background: 'var(--primary)', color: 'white',
                          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4
                        }}>PRINCIPALE</span>
                      )}
                      <button
                        onClick={() => handleRemoveImage(img.id)}
                        style={{
                          position: 'absolute', top: 4, right: 4,
                          background: 'rgba(0,0,0,0.6)', border: 'none',
                          borderRadius: 4, padding: 4, cursor: 'pointer', color: 'white',
                          display: 'flex', alignItems: 'center'
                        }}
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: '24px 16px' }}>
                  <p>Aucune photo. Cliquez sur "Ajouter" pour téléverser des images.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Vidéos ── */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <h2>Vidéos ({unit.videos?.length || 0})</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  {uploadingVideo
                    ? <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    : <FiUpload size={14} />
                  }
                  {uploadingVideo ? ' Upload...' : ' Fichier'}
                  <input
                    type="file"
                    multiple
                    accept="video/*"
                    onChange={handleVideoUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingVideo}
                  />
                </label>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAddVideoUrl(v => !v)}
                >
                  🔗 URL
                </button>
              </div>
            </div>

            {/* Formulaire ajout URL */}
            {showAddVideoUrl && (
              <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', background: '#f8fafc' }}>
                <form onSubmit={handleAddVideoUrl} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: '2 1 240px', margin: 0 }}>
                    <label style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>URL vidéo (YouTube, Vimeo, lien direct…)</label>
                    <input
                      className="form-control"
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={videoUrlInput.url}
                      onChange={e => setVideoUrlInput(v => ({ ...v, url: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group" style={{ flex: '1 1 160px', margin: 0 }}>
                    <label style={{ fontSize: 12, marginBottom: 4, display: 'block' }}>Titre (optionnel)</label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Titre de la vidéo"
                      value={videoUrlInput.title}
                      onChange={e => setVideoUrlInput(v => ({ ...v, title: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 6, paddingBottom: 2 }}>
                    <button type="submit" className="btn btn-primary btn-sm">Ajouter</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddVideoUrl(false)}>
                      <FiX size={14} />
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="card-body">
              {unit.videos && unit.videos.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {unit.videos.map((vid, idx) => {
                    const videoUrl = getFileUrl(vid.url);
                    const isExternal = vid.url?.includes('youtube') || vid.url?.includes('youtu.be') || vid.url?.includes('vimeo');
                    return (
                      <div key={vid.id} style={{
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: '#0f172a',
                        position: 'relative'
                      }}>
                        {isExternal ? (
                          /* Vidéo externe : lien cliquable avec aperçu */
                          <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{
                              width: 56, height: 56, borderRadius: 8,
                              background: 'rgba(255,255,255,0.1)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 28, flexShrink: 0
                            }}>🎥</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: 'white', fontWeight: 600, fontSize: 14, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {vid.title || `Vidéo ${idx + 1}`}
                              </div>
                              <a
                                href={vid.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#60a5fa', fontSize: 12, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', whiteSpace: 'nowrap' }}
                              >
                                {vid.url}
                              </a>
                            </div>
                          </div>
                        ) : (
                          /* Vidéo locale : lecteur natif */
                          <div>
                            {vid.title && (
                              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, padding: '8px 14px 0', fontWeight: 500 }}>
                                🎬 {vid.title}
                              </div>
                            )}
                            <video
                              src={videoUrl}
                              controls
                              preload="metadata"
                              style={{ width: '100%', maxHeight: 280, display: 'block', background: '#000' }}
                            />
                          </div>
                        )}
                        <button
                          onClick={() => handleRemoveVideo(vid.id)}
                          style={{
                            position: 'absolute', top: 8, right: 8,
                            background: 'rgba(220,38,38,0.85)', border: 'none',
                            borderRadius: 6, padding: '5px 8px', cursor: 'pointer',
                            color: 'white', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600
                          }}
                          title="Supprimer cette vidéo"
                        >
                          <FiTrash2 size={12} /> Supprimer
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: '24px 16px' }}>
                  <span style={{ fontSize: 36, opacity: 0.3 }}>🎥</span>
                  <p style={{ marginTop: 8 }}>Aucune vidéo. Téléversez un fichier ou ajoutez une URL YouTube/Vimeo.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal Édition ── */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div style={{
            background: 'var(--bg-white)', borderRadius: 14, padding: 28,
            width: 580, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700 }}>Modifier l'unité {unit.unit_number}</h3>
              <button onClick={() => setShowEditModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 20 }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="grid-2">
                <div className="form-group">
                  <label>N° / Identifiant *</label>
                  <input className="form-control" value={editForm.unit_number}
                    onChange={e => setEditForm({ ...editForm, unit_number: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Étage</label>
                  <input type="number" className="form-control" value={editForm.floor}
                    onChange={e => setEditForm({ ...editForm, floor: e.target.value })} />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Type d'unité</label>
                  <select className="form-control" value={editForm.unit_type}
                    onChange={e => setEditForm({ ...editForm, unit_type: e.target.value })}>
                    <option value="apartment">Appartement</option>
                    <option value="studio">Studio</option>
                    <option value="office">Bureau</option>
                    <option value="shop">Magasin / Commerce</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select className="form-control" value={editForm.status}
                    onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                    <option value="available">Disponible</option>
                    <option value="occupied">Occupé</option>
                    <option value="reserved">Réservé</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="unavailable">Indisponible</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Surface (m²)</label>
                  <input type="number" step="0.01" className="form-control" value={editForm.surface_area}
                    onChange={e => setEditForm({ ...editForm, surface_area: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nb pièces</label>
                  <input type="number" className="form-control" value={editForm.num_rooms}
                    onChange={e => setEditForm({ ...editForm, num_rooms: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nb chambres</label>
                  <input type="number" className="form-control" value={editForm.num_bedrooms}
                    onChange={e => setEditForm({ ...editForm, num_bedrooms: e.target.value })} />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Loyer mensuel (XOF) *</label>
                  <input type="number" className="form-control" value={editForm.monthly_rent}
                    onChange={e => setEditForm({ ...editForm, monthly_rent: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Frais d'agence (XOF)</label>
                  <input type="number" className="form-control" placeholder="ex: 50000" value={editForm.agency_fee}
                    onChange={e => setEditForm({ ...editForm, agency_fee: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Caution (XOF)</label>
                <input type="number" className="form-control" value={editForm.deposit_amount}
                  onChange={e => setEditForm({ ...editForm, deposit_amount: e.target.value })} />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                  <input type="checkbox" checked={editForm.is_furnished}
                    onChange={e => setEditForm({ ...editForm, is_furnished: e.target.checked })} />
                  Unité meublée
                </label>
              </div>
              <div className="form-group">
                <label>Description / Notes</label>
                <textarea className="form-control" rows="3" value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyUnitDetailPage;
